import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';
import syncService from '../services/syncService';
import { API_CONFIG } from '../config/apiConfig';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Verificar autenticação ao carregar
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      console.log('🔐 AuthContext - Verificando status de autenticação...');
      setIsLoading(true);
      setError(null);

      if (authService.isAuthenticated()) {
        console.log('🔐 AuthContext - Usuário autenticado, verificando perfil...');
        // Tentar obter perfil do usuário
        const profileResult = await authService.getProfile();
        
        if (profileResult.success) {
          setUser(profileResult.user);
          setIsAuthenticated(true);
          // Salvar perfil para uso em outros serviços (como dataService)
          localStorage.setItem('auth_user_profile', JSON.stringify(profileResult.user));
          // Iniciar sincronização em segundo plano
          syncService.start(API_CONFIG.SYNC.INTERVAL_MS);
        } else {
          // Se não conseguir obter perfil, limpar autenticação
          await logout();
        }
      } else {
        console.log('🔐 AuthContext - Usuário não autenticado');
        setIsAuthenticated(false);
        setUser(null);
        // Parar sincronização quando não autenticado
        syncService.stop();
      }
    } catch (error) {
      console.error('Erro ao verificar status de autenticação:', error);
      setError('Erro ao verificar autenticação');
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      console.log('🔐 AuthContext - Verificação concluída, isLoading:', false);
      setIsLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await authService.login(credentials);

      if (result.success) {
        setUser(result.user);
        setIsAuthenticated(true);
        // Salvar perfil para uso em outros serviços
        localStorage.setItem('auth_user_profile', JSON.stringify(result.user));
        // Iniciar sincronização em segundo plano
        syncService.start(API_CONFIG.SYNC.INTERVAL_MS);
        return { success: true, message: result.message };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (error) {
      const errorMessage = 'Erro inesperado durante o login';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      setIsLoading(true);
      setError(null);

      await authService.logout();
      
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem('auth_user_profile');
      // Parar sincronização
      syncService.stop();
      
      return { success: true, message: 'Logout realizado com sucesso' };
    } catch (error) {
      console.error('Erro durante logout:', error);
      // Mesmo com erro, limpar estado local
      setUser(null);
      setIsAuthenticated(false);
      return { success: true, message: 'Logout realizado' };
    } finally {
      setIsLoading(false);
    }
  };

  const refreshToken = async () => {
    try {
      const result = await authService.refreshAuthToken();
      
      if (result.success) {
        // Token renovado com sucesso
        return { success: true };
      } else {
        // Token não pôde ser renovado, fazer logout
        await logout();
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('Erro ao renovar token:', error);
      await logout();
      return { success: false, error: 'Erro ao renovar sessão' };
    }
  };

  const clearError = () => {
    setError(null);
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    refreshToken,
    clearError,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
